import React, { useState, useCallback, useEffect } from "react";

import {
    Box,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    FormHelperText,
    Select,
    FormControl,
    InputLabel,
    InputAdornment,
    MenuItem,
    Card,
    styled,
    List,
    ListItem,
    Chip,
    ListItemText,
    OutlinedInput,
    Checkbox,
    Tooltip,
    IconButton
} from '@mui/material';

import {
    LoadingButton
} from '@mui/lab';

import { useNavigate } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from "../../common/CommonBreadcumbs"
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';
import { CommonIcons, icons } from "../../common/CommonIcons";

import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useCustomFields, customFieldModules, customFieldFunctions } from '../../../hooks/api/useCustomFields';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';

import { initialValues as initialValuesBase, validationSchema as validationSchemaBase } from "../../../schema/products/productSchema";
import {
    initialValues as initialValuesUom, initialSettingValues,
    validationSchema as validationSchemaUom, validationSettingSchema
} from "../../../schema/products/itemSchema";
import { inputTypes, inputTypesDef } from "../../../schema/customFields/customFieldSchema"
import { mergeSchema } from "../../../utils/dataSchema"
import { spliceString } from "../../../utils/formatString";

import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useAppContextFeatures } from '../../../context/AppContext';
import { useItems, itemTypes, targetTypes } from "../../../hooks/api/useItems";
import { useUsers, userTypes } from "../../../hooks/api/useUsers";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';

import { useStateCustomFields } from "../../../hooks/state/useStateCustomFields";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewProduct({
    routeType
}) {

    const { promptMessage } = useSystemPrompt();
    const { isLoading, list: listItems, create: createProducts, filterOperatorTypes } = useItems({
        itemType: itemTypes.product,
        targetType: targetTypes.product
    });
    const { list: listCustomFields } = useCustomFields({ customFieldModule: customFieldModules.item, customFieldFunction: customFieldFunctions.item.item })
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.product.id, scopeTypes.product.product.id)

    const {
        dataCustomFields,

        getInitialFormStateRows,
        getSubmitFormDataFromRows,

        getCustomFieldOptionNameByIds,

        RenderCustomFieldSelectionTextbox,
        RenderCustomFieldSelectionDropdown,
        RenderCustomFieldSelectionMultiDropdown
    } = useStateCustomFields(
        {
            fieldModule: customFieldModules.item,
            moduleFunction: customFieldFunctions.item.item
        }
    )

    const { list: getSuppliers } = useUsers({ userType: userTypes.supplier });
    const navigate = useNavigate();
    const { setSystemLoading } = useAppContextFeatures();

    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const [dataBrands, setDataBrands] = useState()
    const [dataUoms, setDataUoms] = useState()
    const [dataSuppliers, setDataSuppliers] = useState()

    const [category, setCategories] = useState({
        level0: [],
        level1: [],
        level2: []
    })

    const validationSchema = mergeSchema(validationSchemaBase, validationSchemaUom, validationSettingSchema);

    const initialValues = {
        ...initialValuesBase,
        ...initialValuesUom,
        ...initialSettingValues
    }

    useEffect(() => {

        fetchDataBrand()
        fetchDataCategory({ level: 0 })
        fetchDataUom()
        fetchDataSuppliers()

    }, [])

    const onSubmit = async (currentValues) => {

        setSystemLoading(true)

        try {

            const { rows } = values;
            let cf = getSubmitFormDataFromRows({ rows })

            // get the correct category id 
            let categoryId = "";

            if (currentValues.categoryLevel2Id) {
                categoryId = `${currentValues.categoryLevel2Id}`
            } else if (currentValues.categoryLevel1Id) {
                categoryId = `${currentValues.categoryLevel1Id}`
            } else if (currentValues.categoryLevel0Id) {
                categoryId = `${currentValues.categoryLevel0Id}`
            }

            let data = {
                product: {
                    item_name: currentValues?.name,
                    model: currentValues?.model,
                    product_code: currentValues?.productCode,
                    description: currentValues?.description,
                    category_id: categoryId,
                    brand_id: currentValues?.brandId,
                    summary: currentValues?.summary,
                    status: currentValues?.status ? 1 : 0,
                },
                item: {
                    is_package: currentValues?.packageStatus ? 1 : 0,
                    status: currentValues?.uomStatus ? 1 : 0,
                    uom_id: currentValues?.uomId ? currentValues.uomId : "",
                    code: currentValues?.code,
                    item_description: currentValues?.uomDesc,
                    remark: currentValues?.remark,
                    cost: currentValues?.cost,
                    uom_price: currentValues?.uomPrice,
                    floor_price: currentValues?.floorPrice,
                    volume_weight: currentValues?.volumeWeight,
                    gross_weight: currentValues?.grossWeight,
                    net_weight: currentValues?.netWeight,
                    dimension_width: currentValues?.width,
                    dimension_height: currentValues?.height,
                    dimension_length: currentValues?.length,
                    cbm: currentValues?.cbm,
                    props: {
                        supplier_id: currentValues?.supplierId,
                        repack_remark: currentValues?.repackRemark,
                        scanning_code_prefix: currentValues?.codePrefix,
                        scanning_code_suffix: currentValues?.codeSuffix,
                        relocate_topup_qty: currentValues.relocateTopUpQty,
                        reorder_topup_qty: currentValues.reorderTopUpQty,
                        relocate_threshold_qty: currentValues.relocateThresholdQty,
                        reorder_threshold_qty: currentValues.reorderThresholdQty,
                    },
                },
                status: {
                    scanning_show_cbm: currentValues?.showCbm ? 1 : 0
                },
                custom_fields: cf
            }

            if (cf.length) {
                data.custom_fields = cf
            }

            const response = await createProducts({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("products/items"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

        setSystemLoading(false)
    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    useEffect(() => {

        setFieldValue(`transformRatio`, "1") // always is this value

    }, [values])

    const loadInitialCustomFieldState = async () => {

        const cf = await getInitialFormStateRows({
            savedCustomFields: []
        })

        setFieldValue(`rows`, cf)
    }

    useEffect(() => {

        loadInitialCustomFieldState()

    }, [])

    const fetchDataCategory = async ({ level, parentId = 0 }) => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [],
                sortInfo: {
                    id: "name",
                    dir: 0,
                }
            }

            if (parentId) {
                props.filterValue = [
                    {
                        name: "parent_category_id",
                        operator: filterOperatorTypes.eq,
                        value: parentId
                    },
                ]
            }

            // for the first level category
            if (level === 0) {

                props.filterValue = [
                    {
                        name: "parent_category_id",
                        operator: filterOperatorTypes.eq,
                        value: '0'
                    },
                ]
            }

            props.itemType = itemTypes.category

            const { data } = await listItems(props)

            // setDataCategories(data?.item_categories)

            if (level === 0) {
                setCategories(prev => ({ ...prev, level0: data?.item_categories }))
            } else if (level === 1) {
                setCategories(prev => ({ ...prev, level1: data?.item_categories }))
            } else if (level === 2) {
                setCategories(prev => ({ ...prev, level2: data?.item_categories }))
            }

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }
    }

    const fetchDataBrand = async () => {

        try {

            const props = {
                limit: -1,
                offset: 0,
                filterValue: [],
                sortInfo: {
                    id: "name",
                    dir: 0,
                }
            }

            props.itemType = itemTypes.brand

            const { data } = await listItems(props)

            setDataBrands(data?.item_brands)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }
    }

    const fetchDataUom = async () => {

        try {

            const props = {
                limit: -1,
                offset: 0,
                filterValue: [],
                sortInfo: {
                    id: "name",
                    dir: 0,
                }
            }

            props.itemType = itemTypes.uom

            const { data } = await listItems(props)

            setDataUoms(data?.item_uoms)

        } catch (error) {
            promptMessage(false, `${error?.message}`)
        }
    }

    const fetchDataSuppliers = async () => {

        try {

            const props = {
                limit: -1,
                skip: 0,
                filterValue: [],
                sortInfo: {
                    id: 'username',
                    dir: ''
                }
            }

            const result = await getSuppliers(props)

            setDataSuppliers(result?.data?.suppliers)

        } catch (error) {
            promptMessage(false, `Action Failed (${error?.message})`)
        }

    }

    const getSupplierOptionNameByIds = (optionIdArray) => {

        if (!optionIdArray) return []

        // customFields.filter(i => i.id === id)

        let optionNames = [];

        dataSuppliers.forEach((e) => {
            // e.options.forEach((e2) => {
            // if(e2.option_key === optionIdArray){
            if (optionIdArray.includes(e.id)) {
                optionNames.push(e.company_name)
            }
            // })
        })

        return optionNames
    }

    const RenderCustomFieldSelection = () => {

        if (!values?.rows) return <></>

        const rows = values.rows

        if (rows.length <= 0) return <></>

        return (
            <>
                {/* {JSON.stringify(rows)} */}
                <Grid item xs={12}>
                    <Divider sx={{ mt: 2, mb: 1 }} />
                </Grid>

                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title='Custom Properties'
                        subTitle={"Provide custom item information"}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    <List>
                        {
                            rows.map((item, index) => {

                                // const { rows } = values

                                // let itemValues = rows.length ? rows[index] : {}
                                let itemValues = item

                                let {
                                    fieldType,
                                    fieldLabel,
                                    fieldKey
                                } = itemValues

                                return (
                                    <ListItem
                                        key={index}
                                        divider
                                        disablePadding
                                        sx={{
                                            py: 1
                                        }}
                                    >
                                        <Grid container spacing={1}>
                                            <Grid item md={3} xs={12} sm={12} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                                                <Chip
                                                    label={fieldLabel}
                                                    color='primary'
                                                    variant='outlined'
                                                    size='large'
                                                    // avatar={<CommonIcons name={icons.add} />}
                                                    sx={{
                                                        width: '100%',
                                                        // textAlign: 'left',
                                                        fontSize: 16,
                                                        fontWeight: 800
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={9} xs={12} sm={12}>

                                                {
                                                    fieldType === inputTypesDef.textbox &&

                                                    RenderCustomFieldSelectionTextbox({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        inputProps: {
                                                            fullWidth: true,
                                                            name: `rows.${index}.customValue`,
                                                            label: itemValues?.fieldLabel,
                                                            type: "text",
                                                            variant: "outlined",
                                                            value: itemValues?.customValue,
                                                            onChange: (event) => setFieldValue(`rows.${index}.customValue`, event.target.value),
                                                        }
                                                    })
                                                }

                                                {
                                                    fieldType === inputTypesDef.dropdown &&

                                                    RenderCustomFieldSelectionDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: "customValue",
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: (event) => {
                                                                setFieldValue(`rows.${index}.customArray`, [event.target.value])
                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            value: itemValues.customArray.length ? itemValues.customArray[0] : ""
                                                        }
                                                    })
                                                }


                                                {
                                                    fieldType === inputTypesDef.multidropdown &&

                                                    RenderCustomFieldSelectionMultiDropdown({
                                                        index,
                                                        item,
                                                        itemValues,
                                                        selectProps: {
                                                            labelId: itemValues?.fieldLabel,
                                                            label: itemValues?.fieldLabel,
                                                            name: `rows.${index}.customArray`,
                                                            onChange: handleChange,
                                                            renderValue: (selected) => {

                                                                return (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {
                                                                            getCustomFieldOptionNameByIds(selected, dataCustomFields, fieldKey).map((i, index) => (
                                                                                <Chip
                                                                                    key={index}
                                                                                    // color={'red'}
                                                                                    label={spliceString({ val: i, maxLength: 20 })}
                                                                                />
                                                                            ))
                                                                        }
                                                                    </Box>
                                                                )

                                                            },
                                                            size: 'small',
                                                            margin: 'dense',
                                                            multiple: true,
                                                            input: <OutlinedInput id="select-multiple-chip" label="Chip" />,
                                                            value: itemValues?.customArray ? itemValues.customArray : []
                                                        }
                                                    })
                                                }

                                            </Grid>
                                        </Grid>
                                    </ListItem>
                                )

                            })
                        }

                    </List>

                </Grid>
            </>
        )
    }

    const RenderFormSettings = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                    <CommonSectionHeader
                        title={'Scanning Information'}
                        subTitle={"Provide detail upon scanning operation"}
                    />

                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>

                        <Grid item xs={12} sm={12} md={12}
                            sx={{
                                display: 'center'
                            }}>
                            <FormControlLabel
                                sx={{
                                    marginLeft: '1px'
                                }}
                                control={
                                    <Switch
                                        name="showCbm"
                                        checked={Boolean(values.showCbm)}
                                        onChange={(event, checked) => {
                                            setFieldValue("showCbm", checked);
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values.showCbm) ? "Show CBM" : "Hide CBM"
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"codePrefix"}
                                label="Code Prefix"
                                type="text"
                                variant="outlined"
                                value={values.codePrefix}
                                onChange={handleChange}
                                error={touched.codePrefix && Boolean(errors.codePrefix)}
                                helperText={touched.codePrefix && errors.codePrefix}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"codeSuffix"}
                                label="Code Suffix"
                                type="text"
                                variant="outlined"
                                value={values.codeSuffix}
                                onChange={handleChange}
                                error={touched.codeSuffix && Boolean(errors.codeSuffix)}
                                helperText={touched.codeSuffix && errors.codeSuffix}
                            />
                        </Grid>


                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderFormMeasurement = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                    <CommonSectionHeader
                        title={'Measurement & Pricing'}
                        subTitle={"Provide detail item information"}
                    />

                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"cost"}
                                label="Cost"
                                type="text"
                                variant="outlined"
                                value={values.cost}
                                onChange={handleChange}
                                error={touched.cost && Boolean(errors.cost)}
                                helperText={touched.cost && errors.cost}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"uomPrice"}
                                label="UOM Price"
                                type="text"
                                variant="outlined"
                                value={values.lastname}
                                onChange={handleChange}
                                error={touched.uomPrice && Boolean(errors.uomPrice)}
                                helperText={touched.uomPrice && errors.uomPrice}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"floorPrice"}
                                label="Floor Price"
                                type="text"
                                variant="outlined"
                                value={values.floorPrice}
                                onChange={handleChange}
                                error={touched.floorPrice && Boolean(errors.floorPrice)}
                                helperText={touched.floorPrice && errors.floorPrice}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"volumeWeight"}
                                label="Volume Weight"
                                type="text"
                                variant="outlined"
                                value={values.volumeWeight}
                                onChange={handleChange}
                                error={touched.volumeWeight && Boolean(errors.volumeWeight)}
                                helperText={touched.volumeWeight && errors.volumeWeight}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"grossWeight"}
                                label="Gross Weight"
                                type="text"
                                variant="outlined"
                                value={values.grossWeight}
                                onChange={handleChange}
                                error={touched.grossWeight && Boolean(errors.grossWeight)}
                                helperText={touched.grossWeight && errors.grossWeight}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"netWeight"}
                                label="Net Weight"
                                type="text"
                                variant="outlined"
                                value={values.netWeight}
                                onChange={handleChange}
                                error={touched.netWeight && Boolean(errors.netWeight)}
                                helperText={touched.netWeight && errors.netWeight}
                            />
                        </Grid>

                        {/* <Grid container spacing={1}
                            sx={{
                                display: 'flex'
                            }}
                        > */}
                        <Grid item xs={4} sm={4} md={2}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"width"}
                                label="Width"
                                type="text"
                                variant="outlined"
                                value={values.width}
                                onChange={handleChange}
                                error={touched.width && Boolean(errors.width)}
                                helperText={touched.width && errors.width}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 800,
                                                    color: 'black'
                                                }}
                                            >W</Typography>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={4} sm={4} md={2}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"height"}
                                label="Height"
                                type="text"
                                variant="outlined"
                                value={values.height}
                                onChange={handleChange}
                                error={touched.height && Boolean(errors.height)}
                                helperText={touched.height && errors.height}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 800,
                                                    color: 'black'
                                                }}
                                            >H</Typography>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={4} sm={4} md={2}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"length"}
                                label="Length"
                                type="text"
                                variant="outlined"
                                value={values.length}
                                onChange={handleChange}
                                error={touched.length && Boolean(errors.length)}
                                helperText={touched.length && errors.length}
                                InputProps={{
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 800,
                                                    color: 'black'
                                                }}
                                            >L</Typography>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>


                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"cbm"}
                                label="CBM"
                                type="text"
                                variant="outlined"
                                value={values.cbm}
                                onChange={handleChange}
                                error={touched.cbm && Boolean(errors.cbm)}
                                helperText={touched.cbm && errors.cbm}
                            />
                        </Grid>
                        {/* </Grid> */}
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderFormUom = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title={'Default UOM'}
                        subTitle={"Provide the default uom of the product"}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>

                        <Grid item xs={12} sm={6} md={3}>
                            <FormControlLabel
                                sx={{
                                    marginLeft: '1px'
                                }}
                                control={
                                    <Switch
                                        name="uomStatus"
                                        // checked={Boolean(values.uomStatus)}
                                        checked={Boolean(true)}
                                        // onChange={(event, checked) => {
                                        //     setFieldValue("uomStatus", checked);
                                        // }}
                                        disabled
                                    />
                                }
                                label={
                                    Boolean(values.uomStatus) ? "Enabled" : "Disabled"
                                }
                            />
                        </Grid>

                        {/* <Grid item xs={12} sm={6} md={3}>
                            <FormControlLabel 
                                sx={{
                                    marginLeft:'1px'
                                }}
                                control={
                                    <Switch 
                                        name="packageStatus" 
                                        checked={Boolean(values.packageStatus)}
                                        onChange={(event, checked) => {
                                            setFieldValue("packageStatus", checked);
                                        }}
                                    />
                                }     
                                label={
                                    Boolean(values.packageStatus)? "Is Package" : "Not Package"
                                }
                            />
                        </Grid> */}

                        <Grid item xs={12} sm={12} md={6}>
                            <></>
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.uomId && Boolean(errors.uomId)}
                            >
                                <InputLabel id="uomId">Uom</InputLabel>
                                <Select
                                    labelId="uomId"
                                    label="Uom"
                                    name={"uomId"}
                                    onChange={(event) => {
                                        setFieldValue("uomId", event.target.value)
                                    }
                                    }
                                    value={values.uomId}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >
                                    {
                                        dataUoms?.map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={i.id}>{i.name}</MenuItem>
                                            )
                                        })
                                    }
                                </Select>
                                <FormHelperText>
                                    {touched.uomId && errors.uomId}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6}><></></Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"code"}
                                label="Code"
                                type="text"
                                variant="outlined"
                                value={values.code}
                                onChange={handleChange}
                                error={touched.code && Boolean(errors.code)}
                                helperText={touched.code && errors.code}
                                InputProps={{
                                    sx: {
                                        pr: 0.5
                                    },
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Tooltip
                                                title={`Unique Identifier`}
                                            >
                                                <IconButton>
                                                    <CommonIcons name={icons.info} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>
                        {/* <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"displayCode"}
                                label="Display Code"
                                type="text"                                         
                                variant="outlined"
                                value={values.displayCode}
                                onChange={handleChange}
                                error={touched.displayCode && Boolean(errors.displayCode)}
                                helperText={touched.displayCode && errors.displayCode}
                            />
                        </Grid> */}

                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.supplierId && Boolean(errors.supplierId)}
                            >
                                <InputLabel id="supplierId">Supplier</InputLabel>

                                <Select
                                    labelId="supplierId"
                                    label="Supplier"
                                    name={"supplierId"}
                                    onChange={(event) => {
                                        setFieldValue("supplierId", event.target.value)
                                    }}
                                    // value={values.supplierId}
                                    value={values.supplierId.length ? values.supplierId : []}
                                    // sx={{
                                    //     textAlign: 'left'
                                    // }}
                                    renderValue={(selected) => {

                                        return (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {
                                                    getSupplierOptionNameByIds(selected).map((i, index) => (
                                                        <Chip
                                                            key={index}
                                                            // color={'red'}
                                                            label={spliceString({ val: i, maxLength: 20 })}
                                                        />
                                                    ))
                                                }
                                            </Box>
                                        )

                                    }}
                                    multiple
                                    input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
                                >
                                    {
                                        dataSuppliers?.map((i, index) => {
                                            return (
                                                <MenuItem
                                                    key={index}
                                                    value={i?.id}
                                                >
                                                    <Checkbox checked={values.supplierId.indexOf(i?.id) > -1} />
                                                    <ListItemText
                                                        primary={`${i.company_name}`}
                                                    />
                                                </MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.supplierId && errors.supplierId}
                                </FormHelperText>

                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"uomDesc"}
                                label="Description"
                                type="text"
                                variant="outlined"
                                value={values.uomDesc}
                                multiline
                                rows={3}
                                onChange={handleChange}
                                error={touched.uomDesc && Boolean(errors.uomDesc)}
                                helperText={touched.uomDesc && errors.uomDesc}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"remark"}
                                label="Remark"
                                type="text"
                                variant="outlined"
                                value={values.remark}
                                multiline
                                rows={3}
                                onChange={handleChange}
                                error={touched.remark && Boolean(errors.remark)}
                                helperText={touched.remark && errors.remark}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"repackRemark"}
                                label="Repack Remark"
                                type="text"
                                variant="outlined"
                                value={values.repackRemark}
                                multiline
                                rows={3}
                                onChange={handleChange}
                                error={touched.repackRemark && Boolean(errors.repackRemark)}
                                helperText={touched.repackRemark && errors.repackRemark}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderTresholdControl = () => {
        return (

            <>

                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>
                    <CommonSectionHeader
                        title='Threshold Control'
                        subTitle={"Set qty of threshold level"}
                    />
                </Grid>

                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={0.5}>

                        <Grid item xs={12} sm={12} md={3} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                            <Chip
                                label={"Reorder"}
                                color='primary'
                                variant='outlined'
                                size='large'
                                sx={{
                                    width: '100%',
                                    // textAlign: 'left',
                                    fontSize: 16,
                                    fontWeight: 800
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4.5}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"reorderThresholdQty"}
                                label="Threshold Qty"
                                type="text"
                                variant="outlined"
                                value={values.reorderThresholdQty}
                                onChange={handleChange}
                                error={touched.reorderThresholdQty && Boolean(errors.reorderThresholdQty)}
                                helperText={touched.reorderThresholdQty && errors.reorderThresholdQty}

                                InputProps={{
                                    sx: {
                                        pr: 0.5
                                    },
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Tooltip
                                                title={`Action perform when threshold qty reach`}
                                            >
                                                <IconButton>
                                                    <CommonIcons name={icons.info} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4.5}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"reorderTopUpQty"}
                                label="Topup Qty"
                                type="text"
                                variant="outlined"
                                value={values.reorderTopUpQty}
                                onChange={handleChange}
                                error={touched.reorderTopUpQty && Boolean(errors.reorderTopUpQty)}
                                helperText={touched.reorderTopUpQty && errors.reorderTopUpQty}

                                InputProps={{
                                    sx: {
                                        pr: 0.5
                                    },
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Tooltip
                                                title={`Suggestion qty to topup when threshold qty reach`}
                                            >
                                                <IconButton>
                                                    <CommonIcons name={icons.info} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={3} display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"} >
                            <Chip
                                label={"Relocate"}
                                color='primary'
                                variant='outlined'
                                size='large'
                                sx={{
                                    width: '100%',
                                    // textAlign: 'left',
                                    fontSize: 16,
                                    fontWeight: 800
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={4.5}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"relocateThresholdQty"}
                                label="Threshold Qty"
                                type="text"
                                variant="outlined"
                                value={values.relocateThresholdQty}
                                onChange={handleChange}
                                error={touched.relocateThresholdQty && Boolean(errors.relocateThresholdQty)}
                                helperText={touched.relocateThresholdQty && errors.relocateThresholdQty}

                                InputProps={{
                                    sx: {
                                        pr: 0.5
                                    },
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Tooltip
                                                title={`Action perform when threshold qty reach`}
                                            >
                                                <IconButton>
                                                    <CommonIcons name={icons.info} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4.5}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"relocateTopUpQty"}
                                label="Topup Qty"
                                type="text"
                                variant="outlined"
                                value={values.relocateTopUpQty}
                                onChange={handleChange}
                                error={touched.relocateTopUpQty && Boolean(errors.relocateTopUpQty)}
                                helperText={touched.relocateTopUpQty && errors.relocateTopUpQty}

                                InputProps={{
                                    sx: {
                                        pr: 0.5
                                    },
                                    endAdornment:
                                        <InputAdornment
                                            position="end"
                                        >
                                            <Tooltip
                                                title={`Suggestion qty to topup when threshold qty reach`}
                                            >
                                                <IconButton>
                                                    <CommonIcons name={icons.info} />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderFormInfo = () => {
        return (
            <>
                <Grid item xs={12} sm={12} md={3} textAlign={"left"}>

                    <CommonSectionHeader
                        title={'Information'}
                        subTitle={"Provide details of the profile"}
                    />

                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControlLabel
                                sx={{
                                    marginLeft: '1px'
                                }}
                                control={
                                    <Switch
                                        name="status"
                                        checked={Boolean(values.status)}
                                        onChange={(event, checked) => {
                                            setFieldValue("status", checked);
                                        }}
                                    />
                                }
                                label={
                                    Boolean(values.status) ? "Enabled" : "Disabled"
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"name"}
                                label="Name"
                                type="text"
                                variant="outlined"
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <></>
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"model"}
                                label="Model"
                                type="text"
                                variant="outlined"
                                value={values.model}
                                onChange={handleChange}
                                error={touched.model && Boolean(errors.model)}
                                helperText={touched.model && errors.model}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                autoFocus
                                fullWidth
                                name={"productCode"}
                                label="Product Code"
                                type="text"
                                variant="outlined"
                                value={values.productCode}
                                onChange={handleChange}
                                error={touched.productCode && Boolean(errors.productCode)}
                                helperText={touched.productCode && errors.productCode}
                            />
                        </Grid>

                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"summary"}
                                label="Summary"
                                type="text"
                                variant="outlined"
                                multiline
                                rows={3}
                                value={values.summary}
                                onChange={handleChange}
                                error={touched.summary && Boolean(errors.summary)}
                                helperText={touched.summary && errors.summary}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <TextField
                                margin="dense"
                                size="small"
                                fullWidth
                                name={"description"}
                                label="Description"
                                type="text"
                                variant="outlined"
                                multiline
                                rows={3}
                                value={values.description}
                                onChange={handleChange}
                                error={touched.description && Boolean(errors.description)}
                                helperText={touched.description && errors.description}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.brandId && Boolean(errors.brandId)}
                            >
                                <InputLabel id="brand">Brand</InputLabel>
                                <Select
                                    labelId="brandId"
                                    label="Brand"
                                    name={"brandId"}
                                    onChange={(event) => {
                                        setFieldValue("brandId", event.target.value)
                                    }
                                    }
                                    value={values.brandId}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >

                                    {
                                        dataBrands?.map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={i.id}>{i.name}</MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.brandId && errors.brandId}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.categoryLevel0Id && Boolean(errors.categoryLevel0Id)}
                            >
                                <InputLabel id="categoryLevel0Id">Category #1</InputLabel>
                                <Select
                                    labelId="categoryLevel0Id"
                                    label="Category"
                                    name={"categoryLevel0Id"}
                                    onChange={(event) => {
                                        setFieldValue("categoryLevel0Id", event.target.value)

                                        fetchDataCategory({ level: 1, parentId: event.target.value })

                                        // clear the child
                                        setCategories(prev => ({ ...prev, level1: [], level2: [] }))
                                        setFieldValue('categoryLevel1Id', "")
                                        setFieldValue('categoryLevel2Id', "")

                                    }}
                                    value={values.categoryLevel0Id}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >
                                    {
                                        category?.level0 && category.level0?.map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={i.id}>{i.name}</MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.categoryLevel0Id && errors.categoryLevel0Id}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.categoryLevel1Id && Boolean(errors.categoryLevel1Id)}
                            >
                                <InputLabel id="categoryLevel1Id">Category #2</InputLabel>
                                <Select
                                    labelId="categoryLevel1Id"
                                    label="Category"
                                    name={"categoryLevel1Id"}
                                    onChange={(event) => {
                                        setFieldValue("categoryLevel1Id", event.target.value)

                                        fetchDataCategory({ level: 2, parentId: event.target.value })

                                        // clear the child
                                        setCategories(prev => ({ ...prev, level2: [] }))
                                        setFieldValue('categoryLevel2Id', "")
                                    }}
                                    value={values.categoryLevel1Id}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >
                                    {
                                        category?.level1 && category.level1?.map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={i.id}>{i.name}</MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.categoryLevel1Id && errors.categoryLevel1Id}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6}>
                            <FormControl
                                margin="dense"
                                fullWidth
                                size="small"
                                error={touched.categoryLevel2Id && Boolean(errors.categoryLevel2Id)}
                            >
                                <InputLabel id="categoryLevel2Id">Category #3</InputLabel>
                                <Select
                                    labelId="categoryLevel2Id"
                                    label="Category"
                                    name={"categoryLevel2Id"}
                                    onChange={(event) => {
                                        setFieldValue("categoryLevel2Id", event.target.value)
                                    }}
                                    value={values.categoryLevel2Id}
                                    sx={{
                                        textAlign: 'left'
                                    }}
                                >
                                    {
                                        category?.level2 && category.level2?.map((i, index) => {
                                            return (
                                                <MenuItem key={index} value={i.id}>{i.name}</MenuItem>
                                            )
                                        })
                                    }

                                </Select>
                                <FormHelperText>
                                    {touched.categoryLevel2Id && errors.categoryLevel2Id}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Grid>
            </>
        )
    }

    const RenderForm = () => {
        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>

                        {
                            RenderFormInfo()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderFormUom()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderFormMeasurement()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderTresholdControl()
                        }

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        {
                            RenderFormSettings()
                        }

                        {/* <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid> */}

                        {
                            RenderCustomFieldSelection()
                        }

                    </Grid>
                </StyledCard>

                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            Save Changes
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newItem })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle="New Product"
                breadcrumbs={[
                    {
                        displayText: "Products",
                        props: {
                            href: getRoute("products/items")
                        }
                    },
                    {
                        displayText: "New Product",
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {/* {JSON.stringify(errors)} */}

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}